import { buildBoundingBoxes } from '@/actions/ReaderActionCreator';
import { ExcerptWidget } from '@/components/widgets/ExcerptWidget';
import { InlineChipWidget } from '@/components/widgets/InlineChipWidget';
import { InlinePaperChipWidget } from '@/components/widgets/InlinePaperChipWidget';
import { InlinePaperFinderWidget } from '@/components/widgets/InlinePaperFinderWidget';
import { InlineReportWidget } from '@/components/widgets/InlineReportWidget';
import { InlineTableWidget } from '@/components/widgets/InlineTableWidget';
import { InlineTopicChipWidget } from '@/components/widgets/InlineTopicChipWidget';
import { Message, MessageId } from '@/models/Message';
import { mkMessageTrackAttrs } from '@/analytics/props/msg';
import { optAppConfig } from '@/utils/config';
import { SyntaxHighlightedCode } from '@/utils/SyntaxHighlightedCode';
import LinkWidget from '@/components/widgets/LinkWidget';

import { compiler } from 'markdown-to-jsx';
import { PropsWithChildren, ReactNode } from 'react';
import Person2Icon from '@mui/icons-material/Person2';
import styled from 'styled-components';

// Add 'md-*' classes to the markdown elements
const MD_OVERRIDES = [
  'a',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'li',
  'ol',
  'p',
  'strong',
  'ul',
].reduce((acc, tag) => {
  acc[tag] = {
    props: {
      className: `md-${tag}`,
    },
  };
  return acc;
}, {} as Record<string, any>);

export function renderMessage(message: Message): ReactNode {
  // Only apply markdown to bot messages
  const botActorId = optAppConfig((_) => _.bot.actor_id);
  if (message.sender.uuid !== botActorId) {
    return (
      <PrewrappedSpan {...mkMessageTrackAttrs({ messageId: message.id })}>
        {message.structuredText}
      </PrewrappedSpan>
    );
  }
  const comps = compiler(message.structuredText, {
    forceWrapper: false,
    overrides: {
      ...MD_OVERRIDES,
      a: {
        props: {
          ...MD_OVERRIDES.a.props,
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      },
      author: {
        component: Author,
      },
      code: {
        component: SyntaxHighlightedCode,
      },
      excerpt: {
        component: Excerpt,
        props: {
          messageId: message.id,
        },
      },
      paper: {
        component: Paper,
      },
      report: {
        component: Report,
        props: {
          messageId: message.id,
        },
      },
      paperfinder: {
        component: PaperFinder,
        props: {
          messageId: message.id,
        },
      },
      table: {
        component: Table,
        props: {
          messageId: message.id,
        },
      },
      topic: {
        component: Topic,
      },
    },
  });
  return (
    <MarkdownMessage {...mkMessageTrackAttrs({ messageId: message.id })}>
      {comps}
    </MarkdownMessage>
  );
}

const Author = ({
  children,
  authorid,
}: PropsWithChildren<{ authorid?: string }>) => {
  if (!authorid) {
    return <span>{children}</span>;
  }
  return (
    <LinkWidget
      url={`https://semanticscholar.org/author/${encodeURIComponent(authorid)}`}
    >
      <InlineChipWidget
        label={getTextFromJSX(children) ?? ''}
        icon={<Person2Icon />}
        isDarkMode={true}
      />
    </LinkWidget>
  );
};

const Excerpt = ({
  children,
  messageId,
  corpusid,
  boundingboxes,
  section,
}: PropsWithChildren<{
  messageId: MessageId;
  corpusid?: string;
  boundingboxes?: string;
  section?: string;
}>) => {
  const excerptText = getTextFromJSX(children) ?? '';
  const corpusId = corpusid ? parseInt(corpusid, 10) : null;
  if (corpusId && excerptText) {
    return (
      <ExcerptWidget
        text={excerptText}
        corpusId={corpusId}
        highlights={buildBoundingBoxes(boundingboxes ?? '')}
        sectionTitle={section}
        messageId={messageId}
      ></ExcerptWidget>
    );
  }

  return excerptText ? <PrewrappedSpan>{excerptText}</PrewrappedSpan> : null;
};

const Paper = ({
  children,
  corpusid,
}: PropsWithChildren<{
  corpusid?: string;
  shortname?: string;
}>) => {
  if (!corpusid) {
    return <span>{children}</span>;
  }
  return (
    <InlinePaperChipWidget
      corpusId={parseInt(corpusid, 10)}
      paperTitle={getTextFromJSX(children) ?? ''}
      isDarkMode={true}
    />
  );
};

const Report = ({
  messageId,
  reportid,
}: PropsWithChildren<{ messageId: MessageId; reportid?: string }>) => {
  if (!reportid) {
    return null;
  }
  return <InlineReportWidget reportId={reportid} messageId={messageId} />;
};

const PaperFinder = ({
  messageId,
  tid,
  wid,
  children,
}: PropsWithChildren<{
  messageId: MessageId;
  tid: string;
  wid: string;
}>) => {
  if (!tid || !wid) {
    return null;
  }
  return (
    <InlinePaperFinderWidget
      messageId={messageId}
      threadId={tid}
      widgetId={wid}
    >
      {children}
    </InlinePaperFinderWidget>
  );
};

const Table = ({
  messageId,
  tableid,
  eventid,
}: PropsWithChildren<{
  messageId: MessageId;
  tableid?: string;
  eventid?: string;
}>) => {
  if (!tableid || !eventid) {
    return null;
  }
  return (
    <InlineTableWidget
      tableId={tableid}
      eventId={eventid}
      messageId={messageId}
    />
  );
};

const Topic = ({
  children,
  topicid,
}: PropsWithChildren<{ topicid?: string }>) => {
  if (!topicid) {
    return <span>{children}</span>;
  }
  return (
    <InlineTopicChipWidget
      topicId={topicid}
      topicName={getTextFromJSX(children) ?? ''}
      isDarkMode={true}
    />
  );
};

export const MarkdownMessage = styled('span')`
  .md-a {
    color: rgb(63, 213, 163);
  }

  .md-h1,
  .md-h2,
  .md-h3,
  .md-h4,
  .md-h5,
  .md-h6 {
    color: #ffffff;
  }
`;

export const PrewrappedSpan = styled('span')`
  white-space: pre-wrap;
`;

// Rip the text content out of a JSX element
export function getTextFromJSX(jsx: ReactNode): string | null {
  if (Array.isArray(jsx)) {
    return jsx
      .map((c) => getTextFromJSX(c))
      .filter(Boolean)
      .join('');
  }
  if (typeof jsx === 'string') {
    return jsx;
  }
  if (typeof jsx === 'number') {
    return jsx.toString();
  }
  if (typeof jsx === 'boolean') {
    return '';
  }
  if (!jsx) {
    return null;
  }
  const children = (jsx as any)?.props?.children;
  if (children) {
    return getTextFromJSX(children);
  }
  return null;
}
